import { Controller } from "@hotwired/stimulus"
import { disconnectObserver, scrollToTop } from "../shared/infinite_scroll"

export default class extends Controller {
  static targets = ["latestResults", "popularResults", "top10sResults", "form", "query", "userQuery", "userResults", "usernameChips"]
  static values = { url: String, top10sUrl: String, searchUsersUrl: String, initialUsers: Array }

  connect() {
    this.debounceTimer = null
    this.userSearchTimer = null
    this.loadingLatest = false
    this.loadingPopular = false
    this.top10sLoaded = false
    this.latestLoaded = false
    this.popularLoaded = false
    this.activeSort = "latest"
    this.selectedUsers = []

    this.boundClickOutside = this.clickOutside.bind(this)
    this.boundPopularTabShown = this.handlePopularTabShown.bind(this)
    this.boundLatestTabShown = this.handleLatestTabShown.bind(this)
    this.boundTop10sTabShown = this.handleTop10sTabShown.bind(this)

    document.addEventListener("click", this.boundClickOutside)

    const popularTabEl = document.getElementById("popular-tab")
    const latestTabEl = document.getElementById("latest-tab")
    const top10sTabEl = document.getElementById("top10s-tab")
    if (popularTabEl) popularTabEl.addEventListener("shown.bs.tab", this.boundPopularTabShown)
    if (latestTabEl) latestTabEl.addEventListener("shown.bs.tab", this.boundLatestTabShown)
    if (top10sTabEl) top10sTabEl.addEventListener("shown.bs.tab", this.boundTop10sTabShown)

    this.initializeUserChips()
    this.restoreFilters()
  }

  disconnect() {
    clearTimeout(this.debounceTimer)
    clearTimeout(this.userSearchTimer)
    document.removeEventListener("click", this.boundClickOutside)

    const popularTabEl = document.getElementById("popular-tab")
    const latestTabEl = document.getElementById("latest-tab")
    const top10sTabEl = document.getElementById("top10s-tab")
    if (popularTabEl) popularTabEl.removeEventListener("shown.bs.tab", this.boundPopularTabShown)
    if (latestTabEl) latestTabEl.removeEventListener("shown.bs.tab", this.boundLatestTabShown)
    if (top10sTabEl) top10sTabEl.removeEventListener("shown.bs.tab", this.boundTop10sTabShown)

    this.disconnectObserver()
  }

  restoreActiveTab(savedTab) {
    const tab = savedTab || "latest"
    const tabEl = document.getElementById(`${tab}-tab`)

    if (tabEl && tabEl.classList.contains("active")) {
      this.activeSort = tab
      if (tab === "top10s") {
        this.fetchTop10s()
      } else {
        this.fetchTab(tab)
      }
    } else if (tabEl) {
      new bootstrap.Tab(tabEl).show()
    } else {
      this.fetchTab("latest")
    }
  }

  saveFilters() {
    const data = {}
    if (this.hasFormTarget) {
      const formData = new FormData(this.formTarget)
      for (const [key, value] of formData.entries()) {
        data[key] = value
      }
    }
    data.tab = this.activeSort
    data.selectedUsers = this.selectedUsers
    localStorage.setItem('bingeflow_fandoms_filters', JSON.stringify(data))
  }

  restoreFilters() {
    const saved = localStorage.getItem('bingeflow_fandoms_filters')
    if (!saved) {
      this.restoreActiveTab(null)
      return
    }
    const data = JSON.parse(saved)
    if (this.hasFormTarget) {
      const form = this.formTarget
      const query = form.querySelector('[name="q"]')
      if (query && data.q) query.value = data.q
      const contentType = form.querySelector('[name="content_type"]')
      if (contentType && data.content_type) contentType.value = data.content_type
      const category = form.querySelector('[name="category"]')
      if (category && data.category) category.value = data.category
      const fandomFilter = form.querySelector('[name="fandom_filter"]')
      if (fandomFilter && data.fandom_filter) fandomFilter.value = data.fandom_filter
    }
    if (data.selectedUsers && data.selectedUsers.length > 0) {
      data.selectedUsers.forEach(user => {
        this.selectedUsers.push(user)
        const chip = document.createElement('span')
        chip.className = 'flow-username-chip'
        chip.dataset.userId = user.id
        chip.innerHTML = `${user.username}<button type="button" data-action="click->fandoms#removeUser" data-user-id="${user.id}">&times;</button>`
        this.usernameChipsTarget.appendChild(chip)
      })
      this.userQueryTarget.placeholder = ''
    }
    this.restoreActiveTab(data.tab)
  }

  handleLatestTabShown() {
    this.activeSort = "latest"
    this.saveFilters()
    if (!this.latestLoaded) {
      this.fetchTab("latest")
    } else {
      this.observeSentinel()
    }
  }

  handlePopularTabShown() {
    this.activeSort = "popular"
    this.saveFilters()
    if (!this.popularLoaded) {
      this.fetchTab("popular")
    } else {
      this.observeSentinel()
    }
  }

  handleTop10sTabShown() {
    this.activeSort = "top10s"
    this.saveFilters()
    if (!this.top10sLoaded) this.fetchTop10s()
  }

  fetchTop10s() {
    if (!this.hasTop10sResultsTarget) return
    this.top10sResultsTarget.innerHTML = '<div class="text-center py-4"><div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div></div>'

    fetch(this.top10sUrlValue, { headers: { "X-Requested-With": "XMLHttpRequest" } })
      .then(response => response.text())
      .then(html => {
        this.top10sResultsTarget.innerHTML = html
        this.top10sLoaded = true
      })
  }

  get activeResultsContainer() {
    return this.activeSort === "popular" ? this.popularResultsTarget : this.latestResultsTarget
  }

  // ── Search & filter ────────────────────────────────────────────────────────

  debounceSearch() {
    clearTimeout(this.debounceTimer)
    const query = this.queryTarget.value.trim()
    if (query.length === 1) return
    this.debounceTimer = setTimeout(() => this.filter(), 500)
  }

  debounceUserSearch() {
    clearTimeout(this.userSearchTimer)
    this.userSearchTimer = setTimeout(() => this.performUserSearch(), 300)
  }

  performUserSearch() {
    const query = this.userQueryTarget.value.trim()
    if (query.length < 2) {
      this.userResultsTarget.classList.add("d-none")
      this.userResultsTarget.innerHTML = ""
      return
    }

    fetch(this.searchUsersUrlValue + "?q=" + encodeURIComponent(query))
      .then(response => response.json())
      .then(data => {
        this.userResultsTarget.innerHTML = ""
        const filtered = data.users.filter(u => !this.selectedUsers.some(s => s.id === u.id))

        if (filtered.length === 0) {
          this.userResultsTarget.innerHTML = '<div class="flow-username-empty">No users found</div>'
        } else {
          filtered.forEach(user => {
            const item = document.createElement("div")
            item.className = "flow-username-item"

            let imageHtml
            if (user.image) {
              imageHtml = `<img src="${user.image}" alt="${user.username}" class="flow-user-image">`
            } else {
              imageHtml = `<div class="flow-user-placeholder">${user.username.charAt(0).toUpperCase()}</div>`
            }

            item.innerHTML = `<div class="d-flex align-items-center gap-2">${imageHtml}<span>${user.username}</span></div>`
            item.addEventListener("click", () => this.selectUser(user))
            this.userResultsTarget.appendChild(item)
          })
        }

        this.userResultsTarget.classList.remove("d-none")
      })
  }

  initializeUserChips() {
    if (!this.hasInitialUsersValue) return

    this.initialUsersValue.forEach(user => {
      this.selectedUsers.push(user)
      const chip = document.createElement("span")
      chip.className = "flow-username-chip"
      chip.dataset.userId = user.id
      chip.innerHTML = `${user.username}<button type="button" data-action="click->fandoms#removeUser" data-user-id="${user.id}">&times;</button>`
      this.usernameChipsTarget.appendChild(chip)
    })

    if (this.selectedUsers.length > 0) {
      this.userQueryTarget.placeholder = ""
    }
  }

  selectUser(user) {
    if (this.selectedUsers.some(u => u.id === user.id)) return

    this.selectedUsers.push(user)

    const chip = document.createElement("span")
    chip.className = "flow-username-chip"
    chip.dataset.userId = user.id
    chip.innerHTML = `${user.username}<button type="button" data-action="click->fandoms#removeUser" data-user-id="${user.id}">&times;</button>`
    this.usernameChipsTarget.appendChild(chip)

    this.userQueryTarget.value = ""
    this.userQueryTarget.placeholder = ""
    this.userResultsTarget.classList.add("d-none")
    this.userResultsTarget.innerHTML = ""

    this.filter()
  }

  removeUser(event) {
    const userId = parseInt(event.currentTarget.dataset.userId)
    this.selectedUsers = this.selectedUsers.filter(u => u.id !== userId)

    const chip = this.usernameChipsTarget.querySelector(`[data-user-id="${userId}"]`)
    if (chip) chip.remove()

    if (this.selectedUsers.length === 0) {
      this.userQueryTarget.placeholder = "Search users..."
    }

    this.filter()
  }

  focusUsernameInput() {
    this.userQueryTarget.focus()
  }

  onUserFocus() {
    const query = this.userQueryTarget.value.trim()
    if (query.length >= 2) this.performUserSearch()
  }

  clickOutside(event) {
    if (this.hasUserQueryTarget && this.hasUserResultsTarget) {
      if (!this.userQueryTarget.contains(event.target) && !this.userResultsTarget.contains(event.target)) {
        this.userResultsTarget.classList.add("d-none")
      }
    }
  }

  buildFilterParams() {
    const params = new URLSearchParams()
    if (this.hasFormTarget) {
      const formData = new FormData(this.formTarget)
      for (const [key, value] of formData.entries()) {
        params.append(key, value)
      }
    }
    this.selectedUsers.forEach(user => {
      params.append("user_ids[]", user.id)
    })
    return params
  }

  filter() {
    this.saveFilters()
    const inactiveSort = this.activeSort === "popular" ? "latest" : "popular"
    const inactiveContainer = inactiveSort === "popular" ? this.popularResultsTarget : this.latestResultsTarget
    if (inactiveSort === "popular") this.popularLoaded = false
    else this.latestLoaded = false
    inactiveContainer.innerHTML = ""
    this.fetchTab(this.activeSort)
  }

  // ── Data fetching ──────────────────────────────────────────────────────────

  fetchTab(sort) {
    const container = sort === "popular" ? this.popularResultsTarget : this.latestResultsTarget

    if (sort === "popular") {
      this.loadingPopular = true
    } else {
      this.loadingLatest = true
    }

    container.innerHTML = '<div class="text-center py-4"><div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div></div>'

    const params = this.buildFilterParams()
    params.set("sort", sort)
    const url = this.urlValue + "?" + params.toString()

    fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
      .then(response => response.text())
      .then(html => {
        container.innerHTML = html

        if (sort === "popular") {
          this.loadingPopular = false
          this.popularLoaded = true
        } else {
          this.loadingLatest = false
          this.latestLoaded = true
        }

        if (this.activeSort === sort) this.observeSentinel()
      })
  }

  loadMore() {
    const sort = this.activeSort
    const isLoading = sort === "popular" ? this.loadingPopular : this.loadingLatest
    if (isLoading) return

    const container = this.activeResultsContainer
    const sentinel = container.querySelector('[data-fandoms-target="sentinel"]')
    if (!sentinel) return

    const nextPage = sentinel.dataset.nextPage
    if (!nextPage) return

    if (sort === "popular") this.loadingPopular = true
    else this.loadingLatest = true

    const spinner = container.querySelector('[data-fandoms-target="spinner"]')
    if (spinner) spinner.classList.remove("d-none")

    const params = this.buildFilterParams()
    params.set("page", nextPage)
    params.set("sort", sort)
    const url = `${this.urlValue}?${params.toString()}`

    fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
      .then(response => response.text())
      .then(html => {
        this.disconnectObserver()

        const currentSpinner = container.querySelector('[data-fandoms-target="spinner"]')
        const currentSentinel = container.querySelector('[data-fandoms-target="sentinel"]')
        const list = container.querySelector('[data-fandoms-target="list"]')

        if (currentSpinner) currentSpinner.remove()
        if (currentSentinel) currentSentinel.remove()

        const temp = document.createElement("div")
        temp.innerHTML = html

        const cards = temp.querySelectorAll(".fandom-card")
        cards.forEach(card => list.appendChild(card))

        const match = html.match(/<!-- next_page:(\d+) -->/)
        if (match) {
          const newSentinel = document.createElement("div")
          newSentinel.dataset.fandomsTarget = "sentinel"
          newSentinel.dataset.nextPage = match[1]
          container.appendChild(newSentinel)

          const newSpinner = document.createElement("div")
          newSpinner.className = "flow-loading text-center py-4 d-none"
          newSpinner.dataset.fandomsTarget = "spinner"
          newSpinner.innerHTML = '<div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div>'
          container.insertBefore(newSpinner, newSentinel)

          this.observeSentinel()
        }

        if (sort === "popular") this.loadingPopular = false
        else this.loadingLatest = false
      })
  }


  // ── Infinite scroll ────────────────────────────────────────────────────────

  observeSentinel() {
    this.disconnectObserver()

    const sentinel = this.activeResultsContainer.querySelector('[data-fandoms-target="sentinel"]')
    if (!sentinel) return

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) this.loadMore()
        })
      },
      { rootMargin: "200px" }
    )

    this.observer.observe(sentinel)
  }

  disconnectObserver() {
    disconnectObserver(this)
  }

  scrollToTop(event) {
    scrollToTop(event)
  }
}
